import { ChangeDetectionStrategy, Component, EventEmitter, inject, Signal } from '@angular/core';
import { ArticleDataService } from '@article/service/article-data.service';
import { ArticleVO } from '@article/model/vo/article.vo';
import { TranslateModule } from '@ngx-translate/core';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { AdminToolService } from '../../../services/admintool.service';
import { EventUtils } from '@shared/util/event-utils';
import { BehaviorSubject, lastValueFrom, of } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { mergeMap } from 'rxjs/operators';

@Component({
  selector: 'app-article-versions',
  imports: [ TranslateModule, ],
  templateUrl: './article-versions.component.html',
  styleUrl: './article-versions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArticleVersionsComponent {
  private adminToolService = inject(AdminToolService);
  private articleDataService = inject(ArticleDataService);
  private alertHelper = inject(AlertHelper);

  public finish = new EventEmitter<void>();

  private readonly _articleVo$ = new BehaviorSubject<ArticleVO | undefined>(undefined);

  public set articleVo(value: ArticleVO) {
    this._articleVo$.next(value);
  }

  public get articleVo(): ArticleVO | undefined {
    return this._articleVo$.value;
  }

  public articlesByVersions: Signal<ArticleVO[]> = toSignal(this._articleVo$.pipe(
    mergeMap((articleVo) => articleVo ? this.articleDataService.getVersionsByArticleId(articleVo.identity) : of<ArticleVO[]>([])),
  ), {requireSync: true});

  private _currentVersion: number | undefined;

  public set currentVersion(value: number) {
    this._currentVersion = value;
    this.selectedVersion = value;
  }

  public get currentVersion(): number | undefined {
    return this._currentVersion;
  }

  public selectedVersion?: number;

  public selectArticleNumber(article: ArticleVO): void {
    this.selectedVersion = article.version;
  }

  public close() {
    this.finish.emit();
  }

  public async deleteLatest() {
    const articlesByVersions = this.articlesByVersions();
    if (articlesByVersions.length < 2) {
      return this.deleteAll();
    }
    const pressedButton = await this.alertHelper.showAlert(
      'manageVersions.button.delete_latest',
      'manageVersions.message.delete_latest',
      {version: articlesByVersions[articlesByVersions.length - 1].version.toString()},
      [AlertButton.OK, AlertButton.CANCEL],
    );
    if (pressedButton === AlertButton.OK && this.articleVo) {
      await lastValueFrom(this.adminToolService.deleteArticleVersion(this.articleVo.identity, this.articlesByVersions()[this.articlesByVersions().length - 1].version));
      this.finish.emit();
      if (this.getLatestVersionNumber() === this.currentVersion) {
        await this.adminToolService.loadArticle(this.articleVo.identity.toString(), this.getPreviousVersionNumber(), true);
      }
    }
  }

  public async deleteAll() {
    const pressedButton = await this.alertHelper.showAlert(
      'manageVersions.button.delete_all',
      'manageVersions.message.delete_all',
      {},
      [ AlertButton.OK, AlertButton.CANCEL ],
    );
    if (pressedButton === AlertButton.OK && this.articleVo) {
      await lastValueFrom(this.adminToolService.deleteArticle(this.articleVo.identity));
      this.finish.emit();
      EventUtils.resetBeforeUnload();
      window.open('admintool', '_self');
    }
  }

  private getPreviousVersionNumber(): number | undefined {
    if (this.articlesByVersions().length < 2) {
      return undefined;
    }
    const articleVersions = this.articlesByVersions()
      .map(article => article.version).sort((a, b) => a - b);
    articleVersions.pop();
    return articleVersions.at(-1);
  }

  private getLatestVersionNumber(): number | undefined {
    if (this.articlesByVersions().length > 0) {
      const articleVersions = this.articlesByVersions()
        .map(article => article.version).sort((a, b) => a - b);
      return articleVersions.at(-1);
    }
  }

  public open() {
    window.open(`admintool#?function=loadarticle&articleNo=${ this.articleVo!.identity }&version=${ this.selectedVersion }`, '_blank');
  }
}
